/*
 * LICENSE PROTECTED SOFTWARE
 *
 * Bu yazilim lisans korumasi altindadir.
 * Gecerli lisans olmadan kullanilmasi yasaktir.
 *
 * Kaynak kodunun izinsiz erisimi, kopyalanmasi veya dagitimi
 * hukuki yaptirimslara tabidir.
 * (c) 2026  @cemdemirci0101
 *  bilgi@cemdemirci.com
 * This source code is proprietary and confidential.
 * Unauthorized copying, modification, or distribution
 * of this file, via any medium, is strictly prohibited.
 * (c) 2026 All Rights Reserved.
 */

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { usePageTitle } from '../hooks/usePageTitle';
import api, { locationsAPI } from '../lib/api';
import { formatApiErrorDetail } from '../lib/utils';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Home, Eye, EyeOff, AlertCircle, ArrowRight, Loader2, UserPlus, Building2, MapPin, Plus, X, ShieldCheck, ScrollText, CheckCircle, Mail, Phone, PhoneCall } from 'lucide-react';
import CaptchaWidget from '../components/CaptchaWidget';
import { toast } from 'sonner';

const KVKK_TEXT = `KENT KONSEYİ DİJİTAL PLATFORMU
KVKK AYDINLATMA METNİ VE AÇIK RIZA BEYANI

1. Veri Sorumlusu
İşbu Aydınlatma Metni, Kent Konseyi Dijital Platformu ("Platform") kapsamında veri sorumlusu sıfatıyla hareket eden ilgili Adana Kent Konseyi tarafından hazırlanmıştır.

2. Kişisel Verilerin Toplanması ve Kategorileri
Platforma üyelik ve kullanım sürecinde aşağıdaki kişisel verileriniz işlenebilir:

a) Kimlik Bilgileri
- Ad, soyad
- Türkiye Cumhuriyeti Kimlik Numarası (TC Kimlik No)
- Doğum tarihi
- Cinsiyet

b) İletişim Bilgileri
- E-posta adresi
- Telefon numarası
- Açık adres (sokak, bina no, daire no dahil)

c) Demografik ve Konum Bilgileri
- İl, ilçe ve mahalle bilgileri
- Eğitim durumu (ilkokul, ortaokul, lise, önlisans, lisans, yüksek lisans)
- Meslek bilgisi
- Mezun olduğunuz veya devam ettiğiniz okulun adı (varsa)
- Üyesi ya da gönüllüsü olduğunuz kuruluş (varsa)

d) Mesleki/Kurumsal Bilgiler (STK temsilcileri için)
- Sivil toplum kuruluşu adı, türü, sicil/DERBİS numarası, vergi numarası
- Kurum telefonu, kurum e-postası, kurum açık adresi, web sitesi
- Faaliyet alanı, üye sayısı, yönetim kurulu bilgileri
- Yetkili temsilci: ad, soyad, T.C. kimlik, görevi, telefon, e-posta
- Yedek temsilci: ad, soyad, T.C. kimlik, görevi, telefon, e-posta
- Başvuru ve sonradan talep edilen belge yüklemeleri (yetki belgesi, tüzük, vb.)

e) Muhtar Bilgileri (Muhtarlar için)
- Görev yaptığı mahalle bilgisi
- Aza sayısı ve aza isimleri (varsa)
- Muhtarlık iletişim bilgileri (telefon, çalışma saatleri)

f) Platform Kullanım Verileri
- Kullanıcı profil bilgileri ve tercihler
- Platform içi etkileşim verileri (öneriler, yorumlar, oy kullanma, katılım kayıtları)
- Anket yanıtları, etkinlik başvuruları, çalışma grubu üyelikleri
- Başvuru ve "sonradan talep edilen belge" akışları kapsamında yüklediğiniz dosyalar (PDF, görsel, vb.)

g) Teknik ve Güvenlik Verileri
- IP adresi, cihaz/tarayıcı bilgisi ve erişim logları
- Kullanıcı eylem geçmişi (audit log)
- KVKK onay tarihi ve IP adresi
- İletişim izni değişiklik geçmişi (tarih, IP, kanal bilgisi)
- Giriş denemeleri ve güvenlik kayıtları
- CAPTCHA doğrulama verileri

3. Kişisel Verilerin İşlenme Amaçları
Kişisel verileriniz aşağıdaki amaçlarla işlenmektedir:
- Üyelik kaydının oluşturulması, kimlik doğrulaması ve yönetilmesi
- E-posta doğrulama sürecinin yürütülmesi
- Çalışma gruplarına ve meclislere katılım süreçlerinin yürütülmesi
- Öneri, proje, anket ve oylama süreçlerinin gerçekleştirilmesi
- Toplantı ve etkinliklerin organize edilmesi, başvuruların yönetilmesi
- Platform içi iletişimin sağlanması (e-posta, SMS, sesli arama bildirimleri)
- Sistem güvenliğinin sağlanması ve yetkisiz erişimin önlenmesi
- Hukuki yükümlülüklerin yerine getirilmesi
- Kent Konseyi faaliyetlerinin planlanması ve yürütülmesi
- Demografik istatistiksel analiz ve raporlama yapılması (cinsiyet, yaş, eğitim dağılımı)
- Excel dışa aktarma ve kurumsal raporlama süreçleri

4. Şeffaflık ve Kamuoyu ile Paylaşım
Kent Konseyi Dijital Platformu, katılımcı demokrasi ve şeffaflık ilkeleri doğrultusunda faaliyet göstermektedir.
Bu kapsamda:
- Çalışma gruplarında üretilen proje ve öneriler
- Anket ve oylama sonuçları
- Alınan kararlar ve toplantı çıktıları
- Projelerin ilerleme durumu ve sonuç raporları
kamuoyu ile paylaşılabilir.
Paylaşımlar şu kanallar üzerinden yapılabilir:
- Kamuya açık platform sayfaları
- İstatistik ve rapor ekranları
- Açık veri (open data) paylaşımları
- Kurumsal bilgilendirme raporları
Bu paylaşımlarda kişisel verileriniz açık rıza olmaksızın açıklanmaz, mümkün olduğunca anonimleştirilir veya toplulaştırılır. E-posta adresleri maskelenerek gösterilir.

5. Kişisel Verilerin Aktarılması ve Üçüncü Taraf Hizmet Sağlayıcılar
Kişisel verileriniz, aşağıdaki sınırlı durumlarda yalnızca belirtilen amaç için üçüncü taraflara aktarılabilir:
- Yetkili kamu kurum ve kuruluşları (Madde 28 / mahkeme veya idari karar gerektiğinde)
- Hukuki yükümlülükler kapsamında yetkili merciler
- E-posta sağlayıcısı (SMTP): Hesap doğrulama, şifre sıfırlama ve bilgilendirme e-postalarınızın gönderilmesi için ad-soyad ve e-posta adresiniz, Veri Sorumlusu tarafından yapılandırılmış SMTP sunucusuna iletilir.
- Platformun teknik altyapı hizmet sağlayıcıları (sunucu/yedekleme), sözleşme ile gizlilik altında.

Yurt İçi ve Yurt Dışı Veri Depolama
Kişisel verileriniz, Veri Sorumlusu'nun kontrolünde, yurt içinde veya yurt dışında bulunan sunucularda saklanabilir ve işlenebilir. Bu kapsamda kullanılabilecek altyapılar:
- Bulut hizmet sağlayıcıları (cloud infrastructure)
- Sunucu ve veri depolama hizmetleri
- Yazılım ve sistem altyapı sağlayıcıları
Yurt dışına aktarım yapılması halinde, KVKK'nın 9. maddesi uyarınca gerekli teknik ve idari güvenlik önlemleri (Fernet/AES-128 şifreleme, sözleşmesel taahhüt vb.) alınarak veriler aktarılır.

6. Veri Toplama Yöntemi ve Hukuki Sebep
Kişisel verileriniz:
- Dijital üyelik formu (vatandaş, muhtar veya STK kayıt formu)
- Platform kullanım işlemleri
- Hesap ayarları güncellemeleri
aracılığıyla otomatik veya kısmen otomatik yollarla toplanmaktadır.
Hukuki sebepler:
- KVKK madde 5/2 kapsamında sözleşmenin kurulması ve ifası
- Hukuki yükümlülüklerin yerine getirilmesi
- Gerekli hallerde açık rıza

7. Kişisel Verilerin Saklanma Süresi
Kişisel verileriniz, ilgili mevzuatta öngörülen süreler boyunca veya işleme amacının gerektirdiği süre kadar saklanır. Hesap silme talebinde bulunulması halinde, kişisel verileriniz anonimleştirilerek 10 yıl süreyle arşivlenir ve süre sonunda kalıcı olarak silinir. Anonimleştirme kapsamında ad, soyad ve e-posta adresiniz maskelenir.
Erişim kayıtları (oturum IP, başarısız giriş denemeleri, audit log) güvenlik ve KVKK Madde 12 (denetim) yükümlülüğü çerçevesinde **en fazla 2 yıl** tutulur, sonrasında otomatik olarak silinir.

8. KVKK Kapsamındaki Haklarınız
KVKK'nın 11. maddesi kapsamında:
- Kişisel verilerinizin işlenip işlenmediğini öğrenme
- İşlenmişse buna ilişkin bilgi talep etme
- Amacına uygun kullanılıp kullanılmadığını öğrenme
- Eksik veya yanlış işlenmişse düzeltilmesini isteme
- Silinmesini veya yok edilmesini talep etme (Hesap Ayarları > Hesabımı Sil)
- Aktarıldığı üçüncü kişileri öğrenme
- İletişim izni tercihlerinizi istediğiniz zaman güncelleme (Hesap Ayarları)
haklarına sahipsiniz.

9. AÇIK RIZA BEYANI
Yukarıda yer alan Aydınlatma Metni kapsamında;
- Kimlik bilgilerimin (ad, soyad, TC Kimlik No, doğum tarihi, cinsiyet) işlenmesini
- İletişim bilgilerimin (e-posta, telefon, açık adres / sokak / bina / daire no dahil) işlenmesini
- Demografik bilgilerimin (eğitim durumu, meslek, mezun olunan/devam edilen okul, üyesi/gönüllüsü olunan kuruluş, il, ilçe, mahalle) işlenmesini
- STK temsilcisi isem; kurum adı, türü, sicil/DERBİS no, vergi no, kurum telefon/e-posta/adres/web sitesi, faaliyet alanı, üye sayısı, yönetim kurulu bilgileri ile yetkili ve yedek temsilci (ad, soyad, T.C. kimlik, görev, telefon, e-posta) bilgilerimin işlenmesini
- Muhtar isem; görev mahallesi, aza sayısı, aza isimleri ve muhtarlık iletişim bilgilerinin işlenmesini
- Kişisel verilerimin belirtilen amaçlarla işlenmesini
- Çalışma grupları, öneri, proje, anket, oylama ve toplantı süreçlerinde kullanılmasını
- Gerekli hallerde yetkili kamu kurumları ile paylaşılmasını
- Platform faaliyetlerinin şeffaflık ilkesi gereği anonimleştirilmiş veya toplulaştırılmış verilerinin kamuoyu ile paylaşılmasını
- Hesap doğrulama / şifre sıfırlama / bilgilendirme e-postalarının iletilmesi için ad-soyad ve e-posta adresimin SMTP (e-posta) sunucusuna aktarılmasını
- Başvuru ve sonradan talep edilen belge yüklemelerimin Veri Sorumlusu'nun şifreli sunucu altyapısında saklanmasını
- Verilerimin Veri Sorumlusu'nun belirleyeceği yurt içi veya yurt dışı sunucu altyapısında depolanmasını ve işlenmesini
- Kent Konseyi faaliyetlerinin raporlanmasını ve istatistiksel olarak değerlendirilmesini
- Hesap silme durumunda verilerimin anonimleştirilerek 10 yıl süreyle arşivlenmesini

10. İLETİŞİM KANALLARI ve TİCARİ ELEKTRONİK İLETİ İZNİ
6563 sayılı Elektronik Ticaretin Düzenlenmesi Hakkında Kanun ve ilgili mevzuat kapsamında, Platform üzerinden tarafınıza:
- E-posta yoluyla bilgilendirme, duyuru ve bildirim mesajları,
- SMS (kısa mesaj) yoluyla önemli güncellemeler ve hatırlatmalar,
- Sesli arama yoluyla acil bildirimler
gönderilebilir.
Bu kanallar üzerinden iletişim kurulabilmesi için açık rızanız gerekmektedir. Kayıt sırasında veya daha sonra Ayarlar sayfanızdan hangi kanallar üzerinden bildirim almak istediğinizi seçebilir, dilediğiniz zaman tercihlerinizi güncelleyebilirsiniz. İzin değişiklikleriniz tarih ve IP bilgisi ile kayıt altına alınır. En az bir iletişim kanalı seçmeniz önerilir.

özgür irademle kabul ediyor ve onaylıyorum.`;

const KVKKDialog = ({ open, onClose, onAccept }) => {
  const scrollRef = useRef(null);
  const [scrolledToBottom, setScrolledToBottom] = useState(false);
  const [accepted, setAccepted] = useState(false);

  const handleScroll = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    const threshold = 40;
    if (el.scrollHeight - el.scrollTop - el.clientHeight < threshold) {
      setScrolledToBottom(true);
    }
  }, []);

  useEffect(() => {
    if (open) { setScrolledToBottom(false); setAccepted(false); }
  }, [open]);

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] flex flex-col p-0">
        <DialogHeader className="px-6 pt-5 pb-3 border-b shrink-0">
          <DialogTitle className="flex items-center gap-2 text-base">
            <ScrollText className="w-5 h-5 text-blue-600" /> KVKK Aydinlatma Metni ve Acik Riza Beyani
          </DialogTitle>
        </DialogHeader>
        <div ref={scrollRef} onScroll={handleScroll}
          className="flex-1 overflow-y-auto px-6 py-4 text-sm text-slate-700 leading-relaxed whitespace-pre-line"
          style={{ maxHeight: '50vh' }}
          data-testid="kvkk-scroll-area">
          {KVKK_TEXT}
        </div>
        <div className="px-6 py-4 border-t bg-slate-50 shrink-0 space-y-3">
          {!scrolledToBottom && (
            <p className="text-xs text-amber-600 flex items-center gap-1.5">
              <AlertCircle className="w-3.5 h-3.5 shrink-0" /> Devam etmek icin metni sonuna kadar okuyunuz
            </p>
          )}
          {scrolledToBottom && (
            <label className="flex items-start gap-2.5 cursor-pointer" data-testid="kvkk-accept-checkbox">
              <input type="checkbox" checked={accepted} onChange={e => setAccepted(e.target.checked)}
                className="mt-0.5 w-4 h-4 rounded border-slate-300 text-blue-600 focus:ring-blue-500" />
              <span className="text-xs text-slate-700 leading-relaxed">
                KVKK Aydinlatma Metni'ni okudum. Kisisel verilerimin belirtilen kapsamda islenmesini, anonimlestirilmis verilerin kamuoyu ile paylasilmasini ve verilerimin yurt disinda bulunan sunucularda depolanabilecegini kabul ediyorum.
              </span>
            </label>
          )}
          <DialogFooter className="sm:justify-between">
            <Button variant="outline" size="sm" onClick={() => onClose(false)}>Vazgec</Button>
            <Button size="sm" disabled={!scrolledToBottom || !accepted} onClick={() => { onAccept(); onClose(false); }}
              className={scrolledToBottom && accepted ? 'bg-emerald-600 hover:bg-emerald-700' : ''}
              data-testid="kvkk-confirm-btn">
              <CheckCircle className="w-4 h-4 mr-1.5" /> Onayliyorum
            </Button>
          </DialogFooter>
        </div>
      </DialogContent>
    </Dialog>
  );
};

const IletisimIzniPanel = ({ data, setData }) => {
  const izni = data.iletisim_izni || { email: false, sms: false, voice: false };
  const toggle = (key) => setData(p => ({ ...p, iletisim_izni: { ...p.iletisim_izni, [key]: !p.iletisim_izni[key] } }));
  const channels = [
    { key: 'email', icon: Mail, label: 'E-posta', desc: 'Duyuru ve bildirimler e-posta ile gönderilsin' },
    { key: 'sms', icon: Phone, label: 'SMS', desc: 'Önemli bildirimler kısa mesaj ile gönderilsin' },
    { key: 'voice', icon: PhoneCall, label: 'Sesli Arama', desc: 'Acil bildirimler sesli arama ile yapılsın' },
  ];
  return (
    <div className="space-y-2" data-testid="iletisim-izni-panel">
      <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide">İletişim Kanalları İzni</p>
      <div className="p-3 rounded-lg border border-slate-200 bg-slate-50 space-y-2">
        <p className="text-xs text-slate-500 leading-relaxed">Hangi kanallardan bildirim almak istediğinizi seçin. Tercihlerinizi daha sonra Ayarlar sayfasından güncelleyebilirsiniz.</p>
        {channels.map(ch => (
          <label key={ch.key} className="flex items-center gap-3 cursor-pointer py-1.5" data-testid={`izni-${ch.key}`}>
            <button type="button" onClick={() => toggle(ch.key)}
              className={`w-5 h-5 rounded border-2 flex items-center justify-center flex-shrink-0 transition-colors ${izni[ch.key] ? 'border-blue-600 bg-blue-600' : 'border-slate-300 bg-white'}`}>
              {izni[ch.key] && <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" /></svg>}
            </button>
            <ch.icon className="w-4 h-4 text-slate-400 flex-shrink-0" />
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-slate-700">{ch.label}</p>
              <p className="text-xs text-slate-400">{ch.desc}</p>
            </div>
          </label>
        ))}
      </div>
    </div>
  );
};

import { STK_KURUM_TURLERI } from '../lib/utils';

const STK_TURLERI = STK_KURUM_TURLERI;

const CINSIYET_OPTIONS = [
  { value: 'erkek', label: 'Erkek' },
  { value: 'kadin', label: 'Kadın' },
  { value: 'belirtmek_istemiyor', label: 'Belirtmek İstemiyorum' },
];

// TC Kimlik Numarasi algoritma dogrulamasi
const validateTCKimlik = (tc) => {
  if (!tc) return { valid: false, error: 'TC Kimlik Numarasi zorunludur' };
  if (!/^\d+$/.test(tc)) return { valid: false, error: 'Sadece rakam giriniz' };
  if (tc.length < 11) return { valid: false, error: `${11 - tc.length} hane daha giriniz` };
  if (tc.length > 11) return { valid: false, error: 'TC Kimlik 11 haneli olmalidir' };
  if (tc[0] === '0') return { valid: false, error: 'TC Kimlik 0 ile baslayamaz' };
  const d = tc.split('').map(Number);
  const oddSum = d[0] + d[2] + d[4] + d[6] + d[8];
  const evenSum = d[1] + d[3] + d[5] + d[7];
  const check10 = (oddSum * 7 - evenSum) % 10;
  if (check10 !== d[9]) return { valid: false, error: 'Gecersiz TC Kimlik Numarasi' };
  const check11 = (d[0]+d[1]+d[2]+d[3]+d[4]+d[5]+d[6]+d[7]+d[8]+d[9]) % 10;
  if (check11 !== d[10]) return { valid: false, error: 'Gecersiz TC Kimlik Numarasi' };
  return { valid: true, error: null };
};

const TCKimlikInput = ({ value, onChange, testId }) => {
  const handleChange = (e) => {
    const val = e.target.value.replace(/\D/g, '').slice(0, 11);
    onChange(val);
  };
  const result = value ? validateTCKimlik(value) : { valid: false, error: null };
  return (
    <div className="space-y-1">
      <Label className="flex items-center gap-1.5">
        <ShieldCheck className="w-3.5 h-3.5 text-slate-500" /> TC Kimlik No *
      </Label>
      <div className="relative">
        <Input
          placeholder="XXXXXXXXXXX"
          maxLength={11}
          value={value}
          onChange={handleChange}
          inputMode="numeric"
          className={value && value.length === 11 ? (result.valid ? 'border-emerald-400 focus-visible:ring-emerald-400' : 'border-red-400 focus-visible:ring-red-400') : ''}
          data-testid={testId || 'tc-kimlik-input'}
        />
        {value && value.length === 11 && result.valid && (
          <span className="absolute right-3 top-1/2 -translate-y-1/2 text-emerald-500"><ShieldCheck className="w-4 h-4" /></span>
        )}
      </div>
      {value && result.error && (
        <p className="text-xs text-red-500" data-testid="tc-error">{result.error}</p>
      )}
    </div>
  );
};

const LocationSelects = ({ data, setData, iller, ilceler, mahalleler, showMahalle = false, mahalleRequired = false }) => (
  <>
    <div className="space-y-2">
      <Label>İl *</Label>
      <Select value={data.il_id} onValueChange={(v) => setData(p => ({ ...p, il_id: v }))}>
        <SelectTrigger><SelectValue placeholder="İl seçin" /></SelectTrigger>
        <SelectContent>{iller.map(il => <SelectItem key={il.id} value={il.id}>{il.name}</SelectItem>)}</SelectContent>
      </Select>
    </div>
    {ilceler.length > 0 && (
      <div className="space-y-2">
        <Label>İlçe *</Label>
        <Select value={data.ilce_id} onValueChange={(v) => setData(p => ({ ...p, ilce_id: v }))}>
          <SelectTrigger><SelectValue placeholder="İlçe seçin" /></SelectTrigger>
          <SelectContent>{ilceler.map(i => <SelectItem key={i.id} value={i.id}>{i.name}</SelectItem>)}</SelectContent>
        </Select>
      </div>
    )}
    {showMahalle && mahalleler && mahalleler.length > 0 && (
      <div className="space-y-2">
        <Label>Mahalle {mahalleRequired ? '*' : ''}</Label>
        <Select value={data.mahalle_id} onValueChange={(v) => setData(p => ({ ...p, mahalle_id: v }))}>
          <SelectTrigger><SelectValue placeholder="Mahalle seçin" /></SelectTrigger>
          <SelectContent>{mahalleler.map(m => <SelectItem key={m.id} value={m.id}>{m.name}</SelectItem>)}</SelectContent>
        </Select>
      </div>
    )}
  </>
);

const PasswordFields = ({ data, setData, showPassword, setShowPassword }) => (
  <div className="grid grid-cols-2 gap-3">
    <div className="space-y-2">
      <Label>Şifre *</Label>
      <div className="relative">
        <Input type={showPassword ? 'text' : 'password'} placeholder="••••••••" value={data.password}
          onChange={(e) => setData(p => ({ ...p, password: e.target.value }))} required data-testid="register-password-input" />
        <button type="button" onClick={() => setShowPassword(!showPassword)}
          className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600">
          {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
        </button>
      </div>
      {data.password && (
        <div className="space-y-0.5 pt-0.5">
          <p className={`text-xs ${data.password.length >= 8 ? 'text-emerald-600' : 'text-slate-400'}`}>{data.password.length >= 8 ? '\u2713' : '\u2022'} En az 8 karakter</p>
          <p className={`text-xs ${/[A-Za-z]/.test(data.password) ? 'text-emerald-600' : 'text-slate-400'}`}>{/[A-Za-z]/.test(data.password) ? '\u2713' : '\u2022'} En az bir harf</p>
          <p className={`text-xs ${/[0-9]/.test(data.password) ? 'text-emerald-600' : 'text-slate-400'}`}>{/[0-9]/.test(data.password) ? '\u2713' : '\u2022'} En az bir rakam</p>
          <p className={`text-xs ${/[^A-Za-z0-9]/.test(data.password) ? 'text-emerald-600' : 'text-slate-400'}`}>{/[^A-Za-z0-9]/.test(data.password) ? '\u2713' : '\u2022'} En az bir ozel karakter (!@#$%)</p>
        </div>
      )}
    </div>
    <div className="space-y-2">
      <Label>Şifre Tekrar *</Label>
      <Input type="password" placeholder="••••••••" value={data.confirmPassword}
        onChange={(e) => setData(p => ({ ...p, confirmPassword: e.target.value }))} required data-testid="register-confirm-password-input" />
      {data.confirmPassword && data.password !== data.confirmPassword && (
        <p className="text-xs text-red-500">Sifreler eslesmiyor</p>
      )}
    </div>
  </div>
);

const GenderSelect = ({ data, setData }) => (
  <div className="space-y-2">
    <Label>Cinsiyet *</Label>
    <Select value={data.cinsiyet} onValueChange={(v) => setData(p => ({ ...p, cinsiyet: v }))}>
      <SelectTrigger data-testid="register-gender-select"><SelectValue placeholder="Cinsiyet seçin" /></SelectTrigger>
      <SelectContent>
        {CINSIYET_OPTIONS.map(c => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}
      </SelectContent>
    </Select>
  </div>
);

const EGITIM_OPTIONS = [
  { value: 'ilkokul', label: 'Ilkokul' },
  { value: 'ortaokul', label: 'Ortaokul' },
  { value: 'lise', label: 'Lise' },
  { value: 'onlisans', label: 'Onlisans' },
  { value: 'lisans', label: 'Lisans' },
  { value: 'yuksek_lisans', label: 'Yuksek Lisans' },
];

const EgitimSelect = ({ data, setData }) => (
  <div className="space-y-2">
    <Label>Egitim Durumu *</Label>
    <Select value={data.egitim_durumu || ''} onValueChange={(v) => setData(p => ({ ...p, egitim_durumu: v }))}>
      <SelectTrigger data-testid="register-egitim-select"><SelectValue placeholder="Egitim durumunuzu secin" /></SelectTrigger>
      <SelectContent>
        {EGITIM_OPTIONS.map(e => <SelectItem key={e.value} value={e.value}>{e.label}</SelectItem>)}
      </SelectContent>
    </Select>
  </div>
);

const MeslekInput = ({ data, setData }) => (
  <div className="space-y-2">
    <Label>Mesleğiniz *</Label>
    <Input
      placeholder="Örn: Öğretmen, Mühendis, Öğrenci, Serbest Meslek..."
      value={data.meslek || ''}
      onChange={(e) => setData(p => ({ ...p, meslek: e.target.value }))}
      required
      maxLength={100}
      data-testid="register-meslek-input"
    />
  </div>
);

const UyeKurulusInput = ({ data, setData }) => (
  <div className="space-y-2">
    <Label>Üyesi ya da gönüllüsü olduğunuz kuruluş (varsa)</Label>
    <Input
      placeholder="Örn: Adana Doğa Derneği, Yeşilay, vb. (opsiyonel)"
      value={data.uyesi_oldugu_kurulus || ''}
      onChange={(e) => setData(p => ({ ...p, uyesi_oldugu_kurulus: e.target.value }))}
      maxLength={150}
      data-testid="register-uye-kurulus-input"
    />
  </div>
);

const OkulAdiInput = ({ data, setData }) => (
  <div className="space-y-2">
    <Label>Mezun olduğunuz veya devam ettiğiniz okulun adı (varsa)</Label>
    <Input
      placeholder="Örn: Çukurova Üniversitesi, Adana Anadolu Lisesi... (opsiyonel)"
      value={data.okul_adi || ''}
      onChange={(e) => setData(p => ({ ...p, okul_adi: e.target.value }))}
      maxLength={150}
      data-testid="register-okul-adi-input"
    />
  </div>
);

const LoginPage = () => {
  const navigate = useNavigate();
  const { login, register, registerMuhtar, registerSTK, isAuthenticated, loading: authLoading } = useAuth();
  usePageTitle('Giris');

  const [activeTab, setActiveTab] = useState('login');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Login form
  const [loginData, setLoginData] = useState({ email: '', password: '' });

  // Vatandaş register form
  const [registerData, setRegisterData] = useState({
    email: '', password: '', confirmPassword: '', name: '', surname: '',
    phone: '', tc_kimlik: '', dogum_tarihi: '', il_id: '', ilce_id: '', mahalle_id: '', acik_adres: '', cinsiyet: '', egitim_durumu: '', meslek: '', uyesi_oldugu_kurulus: '', okul_adi: '',
    kvkk_onay: false,
    iletisim_izni: { email: false, sms: false, voice: false },
  });

  // Muhtar register form
  const [muhtarData, setMuhtarData] = useState({
    email: '', password: '', confirmPassword: '', name: '', surname: '',
    phone: '', tc_kimlik: '', dogum_tarihi: '', il_id: '', ilce_id: '', mahalle_id: '', cinsiyet: '', egitim_durumu: '', meslek: '', okul_adi: '',
    kvkk_onay: false,
    iletisim_izni: { email: false, sms: false, voice: false },
    aza_sayisi: '', aza_isimleri: [''],
    muhtarlik_adresi: '', muhtarlik_telefon: '', calisma_saatleri: '',
  });

  // STK register form
  const [stkData, setSTKData] = useState({
    email: '', password: '', confirmPassword: '', name: '', surname: '', phone: '', cinsiyet: '', egitim_durumu: '', meslek: '', okul_adi: '',
    tc_kimlik: '', dogum_tarihi: '', kvkk_onay: false,
    iletisim_izni: { email: false, sms: false, voice: false },
    stk_adi: '', stk_turu: '', stk_faaliyet_alani: '', stk_adres: '',
    stk_telefon: '', stk_web: '', il_id: '', ilce_id: '',
  });

  const [kvkkDialogOpen, setKvkkDialogOpen] = useState(false);
  const [kvkkTarget, setKvkkTarget] = useState(null);
  const [captchaData, setCaptchaData] = useState({ id: null, answer: null });
  const [captchaReset, setCaptchaReset] = useState(0);
  const handleCaptchaVerified = useCallback((id, answer) => {
    setCaptchaData({ id, answer });
  }, []);
  const [forgotOpen, setForgotOpen] = useState(false);
  const [forgotEmail, setForgotEmail] = useState('');
  const [forgotLoading, setForgotLoading] = useState(false);
  const [forgotSent, setForgotSent] = useState(false); // 'vatandas' | 'muhtar' | 'stk'
  const [showVerification, setShowVerification] = useState(false);
  const [verificationEmail, setVerificationEmail] = useState('');
  const [verificationCode, setVerificationCode] = useState('');
  const [verifyLoading, setVerifyLoading] = useState(false);

  // Location data for each form
  const [iller, setIller] = useState([]);
  const [regIlceler, setRegIlceler] = useState([]);
  const [regMahalleler, setRegMahalleler] = useState([]);
  const [muhtarIlceler, setMuhtarIlceler] = useState([]);
  const [muhtarMahalleler, setMuhtarMahalleler] = useState([]);
  const [stkIlceler, setSTKIlceler] = useState([]);

  useEffect(() => {
    if (isAuthenticated && !authLoading) navigate('/panel');
  }, [isAuthenticated, authLoading, navigate]);

  // URL'den dogrulama token'i oku (e-posta linkinden gelen)
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const urlToken = params.get('token');
    const urlEmail = params.get('email');
    if (urlToken && window.location.pathname === '/dogrula') {
      const doAutoVerify = async () => {
        setShowVerification(true);
        setVerifyLoading(true);
        if (urlEmail) setVerificationEmail(urlEmail);
        try {
          if (!urlEmail) throw new Error('E-posta eksik');
          await api.post('/auth/verify-email', { token: urlToken, email: urlEmail });
          toast.success('E-posta dogrulandi! Artik giris yapabilirsiniz.');
          setShowVerification(false);
          navigate('/giris', { replace: true });
        } catch (err) {
          // Otomatik dogrulama basarisiz - kodu gosterip manuel girmeye yonlendir
          setVerificationCode(urlToken.length > 8 ? '' : urlToken);
          setError(formatApiErrorDetail(err.response?.data?.detail) || 'Dogrulama basarisiz. Lutfen kodu manuel girin.');
        } finally { setVerifyLoading(false); }
      };
      doAutoVerify();
    }
  }, []);

  useEffect(() => { loadIller(); }, []);

  // Vatandaş location chain
  useEffect(() => {
    if (registerData.il_id) {
      loadIlceler(registerData.il_id, setRegIlceler);
      setRegisterData(p => ({ ...p, ilce_id: '', mahalle_id: '' }));
      setRegMahalleler([]);
    }
  }, [registerData.il_id]);
  useEffect(() => {
    if (registerData.ilce_id) loadMahalleler(registerData.ilce_id, setRegMahalleler);
  }, [registerData.ilce_id]);

  // Muhtar location chain
  useEffect(() => {
    if (muhtarData.il_id) {
      loadIlceler(muhtarData.il_id, setMuhtarIlceler);
      setMuhtarData(p => ({ ...p, ilce_id: '', mahalle_id: '' }));
      setMuhtarMahalleler([]);
    }
  }, [muhtarData.il_id]);
  useEffect(() => {
    if (muhtarData.ilce_id) loadMahalleler(muhtarData.ilce_id, setMuhtarMahalleler);
  }, [muhtarData.ilce_id]);

  // STK location chain
  useEffect(() => {
    if (stkData.il_id) {
      loadIlceler(stkData.il_id, setSTKIlceler);
      setSTKData(p => ({ ...p, ilce_id: '' }));
    }
  }, [stkData.il_id]);

  const loadIller = async () => {
    try {
      const response = await locationsAPI.getIller();
      setIller(response.data);
      const adana = response.data.find(il => il.name === 'Adana');
      if (adana) {
        setRegisterData(p => ({ ...p, il_id: adana.id }));
        setMuhtarData(p => ({ ...p, il_id: adana.id }));
        setSTKData(p => ({ ...p, il_id: adana.id }));
      }
    } catch (err) { console.error('Failed to load iller:', err); }
  };

  const loadIlceler = async (ilId, setter) => {
    try { const r = await locationsAPI.getIlceler(ilId); setter(r.data); }
    catch (err) { console.error('Failed to load ilceler:', err); }
  };

  const loadMahalleler = async (ilceId, setter) => {
    try { const r = await locationsAPI.getMahalleler(ilceId); setter(r.data); }
    catch (err) { console.error('Failed to load mahalleler:', err); }
  };

  const validatePasswords = (pw, cpw) => {
    if (pw !== cpw) { setError('Şifreler eşleşmiyor'); return false; }
    if (pw.length < 8) { setError('Sifre en az 8 karakter olmalidir'); return false; }
    if (!/[A-Za-z]/.test(pw)) { setError('Sifre en az bir harf icermeli'); return false; }
    if (!/[0-9]/.test(pw)) { setError('Sifre en az bir rakam icermeli'); return false; }
    if (!/[^A-Za-z0-9]/.test(pw)) { setError('Sifre en az bir ozel karakter icermeli (!@#$%^&*)'); return false; }
    return true;
  };

  const handleLogin = async (e) => {
    e.preventDefault(); setError(''); setLoading(true);
    try { await login((loginData.email || '').trim(), (loginData.password || '').trim()); navigate('/panel'); }
    catch (err) {
      const detail = err.response?.data?.detail || '';
      if (err.response?.status === 403 && detail.includes('dogrulanmamis')) {
        setVerificationEmail(loginData.email);
        setShowVerification(true);
        setError('');
      } else {
        setError(formatApiErrorDetail(detail) || 'Giris yapilamadi');
      }
    }
    finally { setLoading(false); }
  };

  const handleVerifyEmail = async (e) => {
    e.preventDefault(); setError(''); setVerifyLoading(true);
    try {
      const emailToUse = (verificationEmail || loginData.email || '').trim();
      if (!emailToUse) throw new Error('E-posta adresi gerekli');
      await api.post('/auth/verify-email', { token: verificationCode, email: emailToUse });
      setShowVerification(false);
      setError('');
      toast.success('E-posta dogrulandi! Artik giris yapabilirsiniz.');
    } catch (err) { setError(formatApiErrorDetail(err.response?.data?.detail) || 'Dogrulama basarisiz'); }
    finally { setVerifyLoading(false); }
  };

  const handleResendVerification = async () => {
    setVerifyLoading(true);
    try {
      await api.post('/auth/resend-verification', { email: verificationEmail });
      toast.success('Yeni dogrulama kodu gonderildi');
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || 'Gonderilemedi'); }
    finally { setVerifyLoading(false); }
  };

  const handleRegister = async (e) => {
    e.preventDefault(); setError('');
    if (!validatePasswords(registerData.password, registerData.confirmPassword)) return;
    const tcResult = validateTCKimlik(registerData.tc_kimlik);
    if (!tcResult.valid) { setError(tcResult.error); return; }
    if (!registerData.dogum_tarihi) { setError('Dogum tarihi zorunludur'); return; }
    // Tarih araligi kontrolu (client-side UX)
    if (registerData.dogum_tarihi < '1900-01-01') { setError('Dogum tarihi 1900 yilindan once olamaz'); return; }
    if (registerData.dogum_tarihi > new Date().toISOString().slice(0, 10)) { setError('Dogum tarihi gelecek bir tarih olamaz'); return; }
    if (!registerData.mahalle_id) { setError('Mahalle secimi zorunludur'); return; }
    if (!registerData.egitim_durumu) { setError('Egitim durumu secimi zorunludur'); return; }
    if (!registerData.meslek || registerData.meslek.trim().length < 2) { setError('Meslek bilgisi zorunludur (en az 2 karakter)'); return; }
    if (!registerData.kvkk_onay) { setError('KVKK aydinlatma metnini onaylamaniz zorunludur'); return; }
    if (!captchaData.id || captchaData.answer === null) { setError('Guvenlik kodunu cevaplayin'); return; }
    setLoading(true);
    try {
      const { confirmPassword, ...data } = registerData;
      const resp = await register({ ...data, captcha_id: captchaData.id, captcha_answer: captchaData.answer });
      setVerificationEmail(registerData.email);
      // 15 yas alti: Kent Konseyi onayi bekleyecek ama once e-posta dogrulanmali
      if (resp?.requires_kk_approval) {
        toast.info('Kayit alindi. 15 yasin altinda oldugunuz icin basvurunuz Adana Kent Konseyi onayindan gececektir. Oncelikle e-posta adresinizi dogrulayin.', { duration: 10000 });
      }
      setShowVerification(true);
    } catch (err) { setError(formatApiErrorDetail(err.response?.data?.detail) || 'Kayit yapilamadi'); setCaptchaReset(p => p + 1); }
    finally { setLoading(false); }
  };

  const handleMuhtarRegister = async (e) => {
    e.preventDefault(); setError('');
    if (!validatePasswords(muhtarData.password, muhtarData.confirmPassword)) return;
    if (!muhtarData.il_id || !muhtarData.ilce_id) { setError('Lütfen il ve ilçe seçin'); return; }
    if (!muhtarData.mahalle_id) { setError('Mahalle secimi zorunludur'); return; }
    if (!muhtarData.phone) { setError('Telefon numarası zorunludur'); return; }
    const tcResult = validateTCKimlik(muhtarData.tc_kimlik);
    if (!tcResult.valid) { setError(tcResult.error); return; }
    if (!muhtarData.dogum_tarihi) { setError('Dogum tarihi zorunludur'); return; }
    if (!muhtarData.egitim_durumu) { setError('Egitim durumu secimi zorunludur'); return; }
    if (!muhtarData.meslek || muhtarData.meslek.trim().length < 2) { setError('Meslek bilgisi zorunludur (en az 2 karakter)'); return; }
    if (!muhtarData.kvkk_onay) { setError('KVKK aydinlatma metnini onaylamaniz zorunludur'); return; }
    if (!captchaData.id || captchaData.answer === null) { setError('Guvenlik kodunu cevaplayin'); return; }
    setLoading(true);
    try {
      const { confirmPassword, aza_sayisi, ...rest } = muhtarData;
      const payload = {
        ...rest,
        captcha_id: captchaData.id,
        captcha_answer: captchaData.answer,
        aza_sayisi: aza_sayisi ? parseInt(aza_sayisi) : null,
        aza_isimleri: muhtarData.aza_isimleri.filter(a => a.trim()),
      };
      await registerMuhtar(payload);
      setVerificationEmail(muhtarData.email);
      setShowVerification(true);
    } catch (err) { setError(formatApiErrorDetail(err.response?.data?.detail) || 'Kayit yapilamadi'); setCaptchaReset(p => p + 1); }
    finally { setLoading(false); }
  };

  const handleSTKRegister = async (e) => {
    e.preventDefault(); setError('');
    if (!validatePasswords(stkData.password, stkData.confirmPassword)) return;
    if (!stkData.stk_adi) { setError('STK adı zorunludur'); return; }
    if (!stkData.stk_turu) { setError('STK türü zorunludur'); return; }
    const tcResult = validateTCKimlik(stkData.tc_kimlik);
    if (!tcResult.valid) { setError(tcResult.error); return; }
    if (!stkData.dogum_tarihi) { setError('Dogum tarihi zorunludur'); return; }
    if (!stkData.egitim_durumu) { setError('Egitim durumu secimi zorunludur'); return; }
    if (!stkData.meslek || stkData.meslek.trim().length < 2) { setError('Meslek bilgisi zorunludur (en az 2 karakter)'); return; }
    if (!stkData.kvkk_onay) { setError('KVKK aydinlatma metnini onaylamaniz zorunludur'); return; }
    if (!captchaData.id || captchaData.answer === null) { setError('Guvenlik kodunu cevaplayin'); return; }
    setLoading(true);
    try {
      const { confirmPassword, ...data } = stkData;
      await registerSTK({ ...data, captcha_id: captchaData.id, captcha_answer: captchaData.answer });
      setVerificationEmail(stkData.email);
      setShowVerification(true);
    } catch (err) { setError(formatApiErrorDetail(err.response?.data?.detail) || 'Kayit yapilamadi'); setCaptchaReset(p => p + 1); }
    finally { setLoading(false); }
  };

  // Muhtar aza yönetimi
  const addAza = () => setMuhtarData(p => ({ ...p, aza_isimleri: [...p.aza_isimleri, ''] }));
  const removeAza = (idx) => setMuhtarData(p => ({ ...p, aza_isimleri: p.aza_isimleri.filter((_, i) => i !== idx) }));

  const openKvkkDialog = (target) => { setKvkkTarget(target); setKvkkDialogOpen(true); };
  const handleKvkkAccept = () => {
    if (kvkkTarget === 'vatandas') setRegisterData(p => ({ ...p, kvkk_onay: true }));
    else if (kvkkTarget === 'muhtar') setMuhtarData(p => ({ ...p, kvkk_onay: true }));
    else if (kvkkTarget === 'stk') setSTKData(p => ({ ...p, kvkk_onay: true }));
  };

  const handleForgotPassword = async () => {
    if (!forgotEmail) { toast.error('E-posta adresi girin'); return; }
    setForgotLoading(true);
    try {
      await api.post('/auth/forgot-password', { email: forgotEmail });
      setForgotSent(true);
      toast.success('Sifre sifirlama linki gonderildi');
    } catch { toast.error('Islem basarisiz'); }
    finally { setForgotLoading(false); }
  };
  const updateAza = (idx, val) => setMuhtarData(p => {
    const newAza = [...p.aza_isimleri]; newAza[idx] = val;
    return { ...p, aza_isimleri: newAza };
  });

  if (authLoading) {
    return <div className="min-h-screen flex items-center justify-center bg-slate-50"><Loader2 className="w-8 h-8 animate-spin text-blue-600" /></div>;
  }

  return (
    <>
    <div className="min-h-screen flex">
      {/* Left side - Image */}
      <div className="hidden lg:flex lg:w-1/2 bg-slate-950 relative overflow-hidden"
        style={{
          backgroundImage: 'url(/images/login-bg.png)',
          backgroundSize: 'cover', backgroundPosition: 'center',
        }}>
        <div className="absolute inset-0 bg-gradient-to-br from-slate-950/90 to-blue-950/70" />
        <div className="relative z-10 flex flex-col justify-between p-12 text-white">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-xl bg-blue-600 flex items-center justify-center">
              <Home className="w-7 h-7" />
            </div>
            <div>
              <h1 className="text-2xl font-bold">Kent Konseyi</h1>
              <p className="text-sm text-slate-400">Dijital Katılım Platformu</p>
            </div>
          </div>
          <div className="space-y-6">
            <h2 className="text-4xl font-bold leading-tight">Şehrinizin<br />Geleceğine<br />Katılın</h2>
            <p className="text-lg text-slate-300 max-w-md">
              Kent Konseyi Dijital Katılım Platformu ile taleplerınızı iletin,
              önerilerinizi paylaşın ve şehrinizin kararlarına ortak olun.
            </p>
            <div className="flex gap-8 text-sm">
              <div><p className="text-3xl font-bold text-blue-400">10K+</p><p className="text-slate-400">Vatandaş</p></div>
              <div><p className="text-3xl font-bold text-blue-400">500+</p><p className="text-slate-400">STK</p></div>
              <div><p className="text-3xl font-bold text-blue-400">95%</p><p className="text-slate-400">Çözüm Oranı</p></div>
            </div>
          </div>
          <p className="text-sm text-slate-500">© 2024 Kent Konseyi. Tüm hakları saklıdır.</p>
        </div>
      </div>

      {/* Right side - Form */}
      <div className="w-full lg:w-1/2 flex items-center justify-center p-4 sm:p-8 bg-slate-50">
        <div className="w-full max-w-lg">
          {/* Mobile logo */}
          <div className="lg:hidden flex items-center justify-center gap-3 mb-6">
            <div className="w-10 h-10 rounded-lg bg-blue-600 flex items-center justify-center">
              <Home className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="text-xl font-bold text-slate-900">Kent Konseyi</h1>
              <p className="text-xs text-slate-500">Dijital Katılım</p>
            </div>
          </div>

          <Card className="border-0 shadow-lg">
            {showVerification ? (
              <div className="p-6">
                <div className="text-center mb-6">
                  <div className="w-16 h-16 rounded-full bg-blue-100 flex items-center justify-center mx-auto mb-4">
                    <Mail className="w-8 h-8 text-blue-600" />
                  </div>
                  <h2 className="text-xl font-bold text-slate-900">E-posta Dogrulamasi</h2>
                  <p className="text-sm text-slate-500 mt-1">{verificationEmail} adresine gonderilen 6 haneli kodu girin</p>
                </div>
                {error && (
                  <Alert variant="destructive" className="mb-4">
                    <AlertCircle className="h-4 w-4" />
                    <AlertDescription>{error}</AlertDescription>
                  </Alert>
                )}
                <form onSubmit={handleVerifyEmail} className="space-y-4">
                  <div>
                    <Label>Dogrulama Kodu</Label>
                    <Input
                      placeholder="6 haneli kodu girin"
                      value={verificationCode}
                      onChange={e => setVerificationCode(e.target.value)}
                      className="text-center text-2xl tracking-[0.3em] font-bold"
                      maxLength={6}
                      data-testid="verification-code-input"
                    />
                  </div>
                  <Button type="submit" className="w-full bg-blue-600 hover:bg-blue-700" disabled={verifyLoading || verificationCode.length < 6} data-testid="verify-submit-btn">
                    {verifyLoading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <CheckCircle className="w-4 h-4 mr-2" />}
                    Dogrula
                  </Button>
                </form>
                <div className="flex items-center justify-between mt-4 pt-4 border-t">
                  <button type="button" onClick={handleResendVerification} className="text-sm text-blue-600 hover:underline" disabled={verifyLoading} data-testid="resend-btn">
                    Kodu tekrar gonder
                  </button>
                  <button type="button" onClick={() => { setShowVerification(false); setError(''); }} className="text-sm text-slate-500 hover:underline">
                    Giris ekranina don
                  </button>
                </div>
              </div>
            ) : (
            <>
            <CardHeader className="text-center pb-2">
              <CardTitle className="text-2xl font-bold text-slate-900">Hoş Geldiniz</CardTitle>
              <CardDescription>Hesabınıza giriş yapın veya yeni hesap oluşturun</CardDescription>
            </CardHeader>
            <CardContent>
              <Tabs value={activeTab} onValueChange={(v) => { setActiveTab(v); setError(''); }}>
                <TabsList className="grid w-full grid-cols-4 mb-4 h-auto">
                  <TabsTrigger value="login" className="text-xs px-2 py-2" data-testid="login-tab">Giriş</TabsTrigger>
                  <TabsTrigger value="register" className="text-xs px-2 py-2" data-testid="register-tab">Vatandaş</TabsTrigger>
                  <TabsTrigger value="stk" className="text-xs px-2 py-2" data-testid="stk-register-tab">STK</TabsTrigger>
                  <TabsTrigger value="muhtar" className="text-xs px-2 py-2" data-testid="muhtar-register-tab">Muhtar</TabsTrigger>
                </TabsList>

                {error && (
                  <Alert variant="destructive" className="mb-4">
                    <AlertCircle className="h-4 w-4" />
                    <AlertDescription>{error}</AlertDescription>
                  </Alert>
                )}

                {/* ===== LOGIN ===== */}
                <TabsContent value="login">
                  <form onSubmit={handleLogin} className="space-y-4">
                    <div className="space-y-2">
                      <Label>E-posta</Label>
                      <Input type="email" placeholder="cmd@kentkonseyi.org" value={loginData.email}
                        onChange={(e) => setLoginData({ ...loginData, email: e.target.value })} required data-testid="login-email-input" />
                    </div>
                    <div className="space-y-2">
                      <div className="flex justify-between">
                        <Label>Şifre</Label>
                        <button type="button" onClick={() => { setForgotOpen(true); setForgotSent(false); setForgotEmail(loginData.email || ''); }} className="text-sm text-blue-600 hover:underline" data-testid="forgot-password-btn">Sifremi Unuttum</button>
                      </div>
                      <div className="relative">
                        <Input type={showPassword ? 'text' : 'password'} placeholder="••••••••" value={loginData.password}
                          onChange={(e) => setLoginData({ ...loginData, password: e.target.value })} required data-testid="login-password-input" />
                        <button type="button" onClick={() => setShowPassword(!showPassword)}
                          className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600">
                          {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                        </button>
                      </div>
                    </div>
                    <Button type="submit" className="w-full bg-blue-600 hover:bg-blue-700" disabled={loading} data-testid="login-submit-btn">
                      {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <ArrowRight className="w-4 h-4 mr-2" />}
                      Giriş Yap
                    </Button>
                  </form>
                </TabsContent>

                {/* ===== VATANDAS KAYIT ===== */}
                <TabsContent value="register">
                  <form onSubmit={handleRegister} className="space-y-3 max-h-[60vh] overflow-y-auto pr-1">
                    <div className="grid grid-cols-2 gap-3">
                      <div className="space-y-2"><Label>Ad *</Label>
                        <Input placeholder="Adınız" value={registerData.name}
                          onChange={(e) => setRegisterData({ ...registerData, name: e.target.value })} required data-testid="register-name-input" /></div>
                      <div className="space-y-2"><Label>Soyad *</Label>
                        <Input placeholder="Soyadınız" value={registerData.surname}
                          onChange={(e) => setRegisterData({ ...registerData, surname: e.target.value })} required data-testid="register-surname-input" /></div>
                    </div>
                    <div className="space-y-2"><Label>E-posta *</Label>
                      <Input type="email" placeholder="cmd@kentkonseyi.org" value={registerData.email}
                        onChange={(e) => setRegisterData({ ...registerData, email: e.target.value })} required data-testid="register-email-input" /></div>
                    <div className="space-y-2"><Label>Telefon *</Label>
                      <Input type="tel" placeholder="05XX XXX XX XX" value={registerData.phone}
                        onChange={(e) => setRegisterData({ ...registerData, phone: e.target.value })} required data-testid="register-phone-input" /></div>
                    <TCKimlikInput value={registerData.tc_kimlik} onChange={(v) => setRegisterData(p => ({ ...p, tc_kimlik: v }))} testId="register-tc-input" />
                    <div className="space-y-2"><Label>Doğum Tarihi *</Label>
                      <Input type="date" min="1900-01-01" max={new Date().toISOString().slice(0, 10)} value={registerData.dogum_tarihi} onChange={(e) => setRegisterData(p => ({ ...p, dogum_tarihi: e.target.value }))} required data-testid="register-dogum-tarihi" /></div>
                    <LocationSelects data={registerData} setData={setRegisterData} iller={iller} ilceler={regIlceler} mahalleler={regMahalleler} showMahalle mahalleRequired />
                    {registerData.mahalle_id && (
                      <div className="space-y-2"><Label>Açık Adres</Label>
                        <Textarea rows={2} placeholder="Sokak, bina no, daire no..." value={registerData.acik_adres}
                          onChange={(e) => setRegisterData(p => ({ ...p, acik_adres: e.target.value }))} data-testid="register-acik-adres" /></div>
                    )}
                    <GenderSelect data={registerData} setData={setRegisterData} />
                    <EgitimSelect data={registerData} setData={setRegisterData} />
                    <OkulAdiInput data={registerData} setData={setRegisterData} />
                    <MeslekInput data={registerData} setData={setRegisterData} />
                    <UyeKurulusInput data={registerData} setData={setRegisterData} />
                    <PasswordFields data={registerData} setData={setRegisterData} showPassword={showPassword} setShowPassword={setShowPassword} />
                    {/* KVKK Onay */}
                    <div className={`flex items-center gap-3 p-3 rounded-lg border ${registerData.kvkk_onay ? 'border-emerald-300 bg-emerald-50' : 'border-slate-200 bg-slate-50'}`} data-testid="kvkk-vatandas-area">
                      {registerData.kvkk_onay ? (
                        <div className="flex items-center gap-2 flex-1">
                          <CheckCircle className="w-5 h-5 text-emerald-600 shrink-0" />
                          <span className="text-sm text-emerald-700 font-medium">KVKK Onaylandi</span>
                        </div>
                      ) : (
                        <Button type="button" variant="outline" size="sm" className="w-full" onClick={() => openKvkkDialog('vatandas')} data-testid="kvkk-vatandas-btn">
                          <ScrollText className="w-4 h-4 mr-1.5" /> KVKK Aydinlatma Metnini Oku ve Onayla *
                        </Button>
                      )}
                    </div>
                    <IletisimIzniPanel data={registerData} setData={setRegisterData} />
                    <CaptchaWidget onVerified={handleCaptchaVerified} resetTrigger={captchaReset} />
                    <Button type="submit" className="w-full bg-blue-600 hover:bg-blue-700" disabled={loading} data-testid="register-submit-btn">
                      {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <UserPlus className="w-4 h-4 mr-2" />}
                      Vatandaş Kayıt
                    </Button>
                  </form>
                </TabsContent>

                {/* ===== STK KAYIT ===== */}
                <TabsContent value="stk">
                  <form onSubmit={handleSTKRegister} className="space-y-3 max-h-[60vh] overflow-y-auto pr-1">
                    <div className="p-3 bg-blue-50 rounded-lg border border-blue-200">
                      <p className="text-sm text-blue-800 font-medium flex items-center gap-2">
                        <Building2 className="w-4 h-4" />
                        STK Temsilci Kayıt Formu
                      </p>
                      <p className="text-xs text-blue-600 mt-1">Kuruluş bilgileri ve temsilci bilgilerini girin</p>
                    </div>

                    <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide pt-1">Temsilci Bilgileri</p>
                    <div className="grid grid-cols-2 gap-3">
                      <div className="space-y-2"><Label>Ad *</Label>
                        <Input placeholder="Temsilci adı" value={stkData.name}
                          onChange={(e) => setSTKData({ ...stkData, name: e.target.value })} required /></div>
                      <div className="space-y-2"><Label>Soyad *</Label>
                        <Input placeholder="Temsilci soyadı" value={stkData.surname}
                          onChange={(e) => setSTKData({ ...stkData, surname: e.target.value })} required /></div>
                    </div>
                    <div className="space-y-2"><Label>E-posta *</Label>
                      <Input type="email" placeholder="stk@email.com" value={stkData.email}
                        onChange={(e) => setSTKData({ ...stkData, email: e.target.value })} required data-testid="stk-email-input" /></div>
                    <div className="space-y-2"><Label>Telefon *</Label>
                      <Input type="tel" placeholder="05XX XXX XX XX" value={stkData.phone}
                        onChange={(e) => setSTKData({ ...stkData, phone: e.target.value })} required /></div>
                    <TCKimlikInput value={stkData.tc_kimlik} onChange={(v) => setSTKData(p => ({ ...p, tc_kimlik: v }))} testId="stk-tc-input" />
                    <div className="space-y-2"><Label>Doğum Tarihi *</Label>
                      <Input type="date" min="1900-01-01" max={new Date().toISOString().slice(0, 10)} value={stkData.dogum_tarihi} onChange={(e) => setSTKData(p => ({ ...p, dogum_tarihi: e.target.value }))} required data-testid="stk-dogum-tarihi" /></div>

                    <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide pt-2">Kuruluş Bilgileri</p>
                    <div className="space-y-2"><Label>STK Adı *</Label>
                      <Input placeholder="Kuruluş adı" value={stkData.stk_adi}
                        onChange={(e) => setSTKData({ ...stkData, stk_adi: e.target.value })} required data-testid="stk-name-input" /></div>
                    <div className="space-y-2"><Label>STK Türü *</Label>
                      <Select value={stkData.stk_turu} onValueChange={(v) => setSTKData({ ...stkData, stk_turu: v })}>
                        <SelectTrigger data-testid="stk-type-select"><SelectValue placeholder="Tür seçin" /></SelectTrigger>
                        <SelectContent>
                          {STK_TURLERI.map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2"><Label>Faaliyet Alanı</Label>
                      <Input placeholder="Çevre, Eğitim, Sağlık..." value={stkData.stk_faaliyet_alani}
                        onChange={(e) => setSTKData({ ...stkData, stk_faaliyet_alani: e.target.value })} /></div>
                    <div className="space-y-2"><Label>STK Adresi</Label>
                      <Textarea rows={2} placeholder="Kuruluş adresi" value={stkData.stk_adres}
                        onChange={(e) => setSTKData({ ...stkData, stk_adres: e.target.value })} /></div>
                    <div className="grid grid-cols-2 gap-3">
                      <div className="space-y-2"><Label>STK Telefon</Label>
                        <Input type="tel" placeholder="0322 XXX XX XX" value={stkData.stk_telefon}
                          onChange={(e) => setSTKData({ ...stkData, stk_telefon: e.target.value })} /></div>
                      <div className="space-y-2"><Label>Web Sitesi</Label>
                        <Input placeholder="www.stk.org.tr" value={stkData.stk_web}
                          onChange={(e) => setSTKData({ ...stkData, stk_web: e.target.value })} /></div>
                    </div>
                    <LocationSelects data={stkData} setData={setSTKData} iller={iller} ilceler={stkIlceler} />
                    <GenderSelect data={stkData} setData={setSTKData} />
                    <EgitimSelect data={stkData} setData={setSTKData} />
                    <OkulAdiInput data={stkData} setData={setSTKData} />
                    <MeslekInput data={stkData} setData={setSTKData} />
                    <PasswordFields data={stkData} setData={setSTKData} showPassword={showPassword} setShowPassword={setShowPassword} />
                    {/* KVKK Onay */}
                    <div className={`flex items-center gap-3 p-3 rounded-lg border ${stkData.kvkk_onay ? 'border-emerald-300 bg-emerald-50' : 'border-slate-200 bg-slate-50'}`} data-testid="kvkk-stk-area">
                      {stkData.kvkk_onay ? (
                        <div className="flex items-center gap-2 flex-1">
                          <CheckCircle className="w-5 h-5 text-emerald-600 shrink-0" />
                          <span className="text-sm text-emerald-700 font-medium">KVKK Onaylandi</span>
                        </div>
                      ) : (
                        <Button type="button" variant="outline" size="sm" className="w-full" onClick={() => openKvkkDialog('stk')} data-testid="kvkk-stk-btn">
                          <ScrollText className="w-4 h-4 mr-1.5" /> KVKK Aydinlatma Metnini Oku ve Onayla *
                        </Button>
                      )}
                    </div>
                    <IletisimIzniPanel data={stkData} setData={setSTKData} />
                    <CaptchaWidget onVerified={handleCaptchaVerified} resetTrigger={captchaReset} />
                    <Button type="submit" className="w-full bg-emerald-600 hover:bg-emerald-700" disabled={loading} data-testid="stk-register-submit-btn">
                      {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Building2 className="w-4 h-4 mr-2" />}
                      STK Kaydını Tamamla
                    </Button>
                  </form>
                </TabsContent>

                {/* ===== MUHTAR KAYIT ===== */}
                <TabsContent value="muhtar">
                  <form onSubmit={handleMuhtarRegister} className="space-y-3 max-h-[60vh] overflow-y-auto pr-1">
                    <div className="p-3 bg-amber-50 rounded-lg border border-amber-200">
                      <p className="text-sm text-amber-800 font-medium flex items-center gap-2">
                        <MapPin className="w-4 h-4" />
                        Muhtar Kayıt Formu
                      </p>
                      <p className="text-xs text-amber-600 mt-1">Kişisel, konum ve aza bilgilerini eksiksiz doldurun</p>
                    </div>

                    <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide pt-1">Kişisel Bilgiler</p>
                    <div className="grid grid-cols-2 gap-3">
                      <div className="space-y-2"><Label>Ad *</Label>
                        <Input placeholder="Adınız" value={muhtarData.name}
                          onChange={(e) => setMuhtarData({ ...muhtarData, name: e.target.value })} required data-testid="muhtar-name-input" /></div>
                      <div className="space-y-2"><Label>Soyad *</Label>
                        <Input placeholder="Soyadınız" value={muhtarData.surname}
                          onChange={(e) => setMuhtarData({ ...muhtarData, surname: e.target.value })} required /></div>
                    </div>
                    <div className="space-y-2"><Label>E-posta *</Label>
                      <Input type="email" placeholder="muhtar@email.com" value={muhtarData.email}
                        onChange={(e) => setMuhtarData({ ...muhtarData, email: e.target.value })} required data-testid="muhtar-email-input" /></div>
                    <div className="space-y-2"><Label>Cep Telefonu *</Label>
                      <Input type="tel" placeholder="05XX XXX XX XX" value={muhtarData.phone}
                        onChange={(e) => setMuhtarData({ ...muhtarData, phone: e.target.value })} required /></div>
                    <TCKimlikInput value={muhtarData.tc_kimlik} onChange={(v) => setMuhtarData(p => ({ ...p, tc_kimlik: v }))} testId="muhtar-tc-input" />
                    <div className="space-y-2"><Label>Doğum Tarihi *</Label>
                      <Input type="date" min="1900-01-01" max={new Date().toISOString().slice(0, 10)} value={muhtarData.dogum_tarihi} onChange={(e) => setMuhtarData(p => ({ ...p, dogum_tarihi: e.target.value }))} required data-testid="muhtar-dogum-tarihi" /></div>

                    <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide pt-2">Konum Bilgileri</p>
                    <LocationSelects data={muhtarData} setData={setMuhtarData} iller={iller} ilceler={muhtarIlceler} mahalleler={muhtarMahalleler} showMahalle mahalleRequired />

                    <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide pt-2">Muhtarlık İletişim Bilgileri</p>
                    <div className="space-y-2"><Label>Muhtarlık Adresi</Label>
                      <Textarea rows={2} placeholder="Muhtarlık ofis adresi" value={muhtarData.muhtarlik_adresi}
                        onChange={(e) => setMuhtarData({ ...muhtarData, muhtarlik_adresi: e.target.value })} /></div>
                    <div className="grid grid-cols-2 gap-3">
                      <div className="space-y-2"><Label>Muhtarlık Telefon</Label>
                        <Input type="tel" placeholder="0322 XXX XX XX" value={muhtarData.muhtarlik_telefon}
                          onChange={(e) => setMuhtarData({ ...muhtarData, muhtarlik_telefon: e.target.value })} /></div>
                      <div className="space-y-2"><Label>Çalışma Saatleri</Label>
                        <Input placeholder="09:00 - 17:00" value={muhtarData.calisma_saatleri}
                          onChange={(e) => setMuhtarData({ ...muhtarData, calisma_saatleri: e.target.value })} /></div>
                    </div>

                    <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide pt-2">Muhtar Azaları</p>
                    <div className="space-y-2"><Label>Aza Sayısı</Label>
                      <Input type="number" min="0" max="20" placeholder="Aza sayısı" value={muhtarData.aza_sayisi}
                        onChange={(e) => setMuhtarData({ ...muhtarData, aza_sayisi: e.target.value })} /></div>
                    <div className="space-y-2">
                      <Label>Aza İsimleri</Label>
                      {muhtarData.aza_isimleri.map((aza, idx) => (
                        <div key={`pref-${idx}`} className="flex gap-2 items-center">
                          <Input placeholder={`${idx + 1}. Aza adı soyadı`} value={aza}
                            onChange={(e) => updateAza(idx, e.target.value)}
                            data-testid={`muhtar-aza-${idx}`} />
                          {muhtarData.aza_isimleri.length > 1 && (
                            <Button type="button" variant="ghost" size="icon" className="h-9 w-9 shrink-0 text-red-500 hover:text-red-700 hover:bg-red-50" onClick={() => removeAza(idx)}>
                              <X className="w-4 h-4" />
                            </Button>
                          )}
                        </div>
                      ))}
                      <Button type="button" variant="outline" size="sm" className="w-full border-dashed" onClick={addAza}>
                        <Plus className="w-3 h-3 mr-1" /> Aza Ekle
                      </Button>
                    </div>

                    <GenderSelect data={muhtarData} setData={setMuhtarData} />
                    <EgitimSelect data={muhtarData} setData={setMuhtarData} />
                    <OkulAdiInput data={muhtarData} setData={setMuhtarData} />
                    <MeslekInput data={muhtarData} setData={setMuhtarData} />
                    <PasswordFields data={muhtarData} setData={setMuhtarData} showPassword={showPassword} setShowPassword={setShowPassword} />
                    {/* KVKK Onay */}
                    <div className={`flex items-center gap-3 p-3 rounded-lg border ${muhtarData.kvkk_onay ? 'border-emerald-300 bg-emerald-50' : 'border-slate-200 bg-slate-50'}`} data-testid="kvkk-muhtar-area">
                      {muhtarData.kvkk_onay ? (
                        <div className="flex items-center gap-2 flex-1">
                          <CheckCircle className="w-5 h-5 text-emerald-600 shrink-0" />
                          <span className="text-sm text-emerald-700 font-medium">KVKK Onaylandi</span>
                        </div>
                      ) : (
                        <Button type="button" variant="outline" size="sm" className="w-full" onClick={() => openKvkkDialog('muhtar')} data-testid="kvkk-muhtar-btn">
                          <ScrollText className="w-4 h-4 mr-1.5" /> KVKK Aydinlatma Metnini Oku ve Onayla *
                        </Button>
                      )}
                    </div>
                    <IletisimIzniPanel data={muhtarData} setData={setMuhtarData} />
                    <CaptchaWidget onVerified={handleCaptchaVerified} resetTrigger={captchaReset} />
                    <Button type="submit" className="w-full bg-amber-600 hover:bg-amber-700" disabled={loading} data-testid="muhtar-register-submit-btn">
                      {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <MapPin className="w-4 h-4 mr-2" />}
                      Muhtar Kaydını Tamamla
                    </Button>
                  </form>
                </TabsContent>
              </Tabs>
            </CardContent>
            </>
            )}
          </Card>
        </div>
      </div>
    </div>
    <KVKKDialog open={kvkkDialogOpen} onClose={setKvkkDialogOpen} onAccept={handleKvkkAccept} />
    <Dialog open={forgotOpen} onOpenChange={setForgotOpen}>
      <DialogContent className="max-w-sm">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><ShieldCheck className="w-5 h-5 text-blue-600" /> Sifremi Unuttum</DialogTitle>
        </DialogHeader>
        {forgotSent ? (
          <div className="text-center py-4">
            <CheckCircle className="w-12 h-12 text-emerald-500 mx-auto mb-3" />
            <p className="text-sm text-slate-700">Sifre sifirlama linki <strong>{forgotEmail}</strong> adresine gonderildi.</p>
            <p className="text-xs text-slate-400 mt-2">E-postanizi kontrol edin. Link 1 saat gecerlidir.</p>
          </div>
        ) : (
          <div className="space-y-3 py-2">
            <p className="text-sm text-slate-500">Kayitli e-posta adresinizi girin, size sifre sifirlama linki gonderelim.</p>
            <Input type="email" placeholder="E-posta adresiniz" value={forgotEmail} onChange={e => setForgotEmail(e.target.value)} data-testid="forgot-email-input" />
            <Button onClick={handleForgotPassword} disabled={forgotLoading} className="w-full" data-testid="forgot-submit-btn">
              {forgotLoading ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <ArrowRight className="w-4 h-4 mr-1" />} Sifirlama Linki Gonder
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
    </>
  );
};

export default LoginPage;
